/**
 * Best-effort JS error reporter (v139.2).
 *
 * Fires an unauthenticated POST to /api/js-errors/report so we can see
 * production crashes without waiting for a user to report them.
 *
 * Deliberately dependency-free (uses `fetch` directly, not axios) so it
 * never itself throws — a broken reporter would silence the crashes we're
 * trying to catch.
 *
 * Wired from three places:
 *   1. ErrorBoundary.componentDidCatch (React render errors)
 *   2. window.addEventListener("error")            (runtime errors)
 *   3. window.addEventListener("unhandledrejection") (async errors)
 *
 * Debounced to at most 1 report per (message+url) per 30s in the same
 * tab so a crashing render loop can't hammer the backend.
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const BUILD_VERSION = process.env.REACT_APP_BUILD_VERSION || "dev";

// Per-tab dedup so a crash-loop doesn't fire 100 requests.
const recentKeys = new Map(); // key -> lastSentMs
const DEDUP_WINDOW_MS = 30_000;

function _key(message, url) {
  const firstLine = String(message || "").split("\n")[0].slice(0, 200);
  const path = url ? url.split("?")[0].split("#")[0] : "";
  return `${firstLine}|${path}`;
}

function _currentUserId() {
  try {
    // AuthContext writes the current user_id to `window.__linkolaUserId` on
    // every setUser() so we can read it without introducing an import cycle.
    if (typeof window !== "undefined" && window.__linkolaUserId) {
      return String(window.__linkolaUserId).slice(0, 64);
    }
  } catch {
    /* noop */
  }
  return null;
}

export function reportClientError({ message, stack, kind = "runtime" }) {
  if (!message || !BACKEND) return;
  try {
    const url = typeof window !== "undefined" ? window.location.href : "";
    const key = _key(message, url);
    const now = Date.now();
    const last = recentKeys.get(key) || 0;
    if (now - last < DEDUP_WINDOW_MS) return;
    recentKeys.set(key, now);
    // Best-effort — never await, never .catch() into another crash.
    fetch(`${BACKEND}/api/js-errors/report`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // No credentials — endpoint is public, and we don't want the
      // reporter dragging in auth cookies that could fail during a crash.
      credentials: "omit",
      keepalive: true, // let the request survive a page unload
      body: JSON.stringify({
        message: String(message).slice(0, 1000),
        stack: stack ? String(stack).slice(0, 8000) : null,
        url: url.slice(0, 800) || null,
        user_agent: (typeof navigator !== "undefined" && navigator.userAgent) || null,
        user_id: _currentUserId(),
        kind,
        build_version: BUILD_VERSION,
        client_ts: new Date().toISOString(),
      }),
    }).catch(() => {
      /* silent — never crash while reporting a crash */
    });
  } catch {
    /* silent */
  }
}

/**
 * Attach global runtime + unhandled-promise listeners. Call once from
 * `index.js` right after React mounts.
 */
export function installGlobalErrorReporter() {
  if (typeof window === "undefined") return;
  if (window.__linkolaErrReporterInstalled) return;
  window.__linkolaErrReporterInstalled = true;

  window.addEventListener("error", (e) => {
    reportClientError({
      message: e?.message || String(e?.error || "unknown error"),
      stack: e?.error?.stack,
      kind: "runtime",
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason = e?.reason;
    const message =
      (reason && (reason.message || String(reason))) || "Unhandled promise rejection";
    reportClientError({
      message,
      stack: reason?.stack,
      kind: "promise",
    });
  });
}
